import { Injectable } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { BreadCrumbConfig } from '../models/breadcrumb.model';
import { LocalStorageUtilWrapperService } from './local-storage-util-wrapper.service';
import { CookieUtilWrapperService } from './cookie-util-wrapper.service';
import { ActivatedRouteSnapshot } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ContextUpdatesService {
  successfulContextUpdates: Subject<any> = new Subject();

  optimisticContextUpdates: Subject<any> = new Subject();

  refreshContextSwitcher: Subject<any> = new Subject();

  contextSwitcherUpdated: Subject<any> = new Subject();

  branchDataLoaded: ReplaySubject<any> = new ReplaySubject<any>(1);

  updateBlueprintTabMetaDataOnBranchNameChange: Subject<any> = new Subject();

  allBlueprints: any[];

  allEnvironments: any[];

  blueprintResourceMap: Map<string, Map<string, string>>;

  currentContext: BreadCrumbConfig = {} as BreadCrumbConfig;

  currentURLParams: ActivatedRouteSnapshot;

  URLParamsUpdates: Subject<any> = new Subject<any>();

  // This map stores the last selected branch for a blueprint.
  // Would be initialized from the data stored in local storage as soon as
  // the service is created on load.
  blueprintBranchMap: any = {};
  BRANCH_COOKIE_NAME = 'lastBranchData';

  constructor(
    private localStorageService: LocalStorageUtilWrapperService,
    private cookiesService: CookieUtilWrapperService,
  ) {
    this.clearRedundantCookies();
    this.initializeBranchData();
  }

  clearRedundantCookies() {
    const redundantKeys = [
      'currentContext',
      'previousContext',
      'lastBranchData',
    ];
    redundantKeys.forEach((key: string) => {
      this.cookiesService.delete(key, '/');
    });
  }

  initializeBranchData() {
    this.blueprintBranchMap = this.getLastOpenedBranchMapFromLocalStorage();
  }

  setContextUpdates(
    contexts: {
      currentContext: BreadCrumbConfig;
      previousContext: BreadCrumbConfig;
    },
    status: 'optimistic' | 'successful',
  ) {
    status === 'optimistic'
      ? this.optimisticContextUpdates.next(contexts)
      : this.successfulContextUpdates.next(contexts);
  }

  setCurrentContext(context: BreadCrumbConfig) {
    this.currentContext = context;
  }

  getCurrentContext() {
    return this.currentContext;
  }

  requestContextSwitcherRefresh(extras?: any) {
    this.refreshContextSwitcher.next({ refresh: true, extras: extras });
  }

  notifyContextSwitcherUpdate(extras: any) {
    this.contextSwitcherUpdated.next({ updated: true, extras: extras });
  }

  setCurrentEnvironments(environment: any[]) {
    this.allEnvironments = environment;
  }

  getCurrentEnvironments() {
    return this.allEnvironments;
  }

  setBlueprintResourceMapping(blueprint, resourceMap) {
    if (!this.blueprintResourceMap) {
      this.blueprintResourceMap = new Map<string, Map<string, string>>();
    }
    this.blueprintResourceMap.set(blueprint, resourceMap);
  }

  getResourceMapForBlueprint(blueprint) {
    return this.blueprintResourceMap?.get(blueprint);
  }

  getAllResourcesForBlueprint(blueprint) {
    if (!this.blueprintResourceMap) {
      return null;
    }
    let allResources = [];
    for (const [key, values] of Object.entries(
      this.blueprintResourceMap?.get(blueprint),
    )) {
      allResources.push(...values);
    }
    return allResources;
  }

  setAllBlueprints(blueprints) {
    this.allBlueprints = blueprints;
  }

  getAllBlueprints() {
    return this.allBlueprints;
  }

  setContextInLocalStorage(
    key: 'currentContext' | 'previousContext',
    context: BreadCrumbConfig,
  ) {
    this.localStorageService.set(key, JSON.stringify(context));
  }

  getContextFromLocalStorage(key: 'currentContext' | 'previousContext') {
    let value;
    try {
      value = JSON.parse(this.localStorageService.get(key)) as BreadCrumbConfig;
    } catch {
      value = {} as BreadCrumbConfig;
    }
    return value;
  }

  deleteContextFromLocalStorage(key: 'currentContext' | 'previousContext') {
    this.localStorageService.remove(key);
  }

  setLastOpenedBranchInLocalStorage(blueprint: string, branchName: string) {
    this.blueprintBranchMap[blueprint] = branchName;
    this.localStorageService.set(
      this.BRANCH_COOKIE_NAME,
      JSON.stringify(this.blueprintBranchMap),
    );
  }

  getLastOpenedBranchMapFromLocalStorage() {
    const branchData = this.localStorageService.get(this.BRANCH_COOKIE_NAME);
    if (branchData) {
      try {
        return JSON.parse(branchData);
      } catch (err) {
        return {};
      }
    } else {
      return {};
    }
  }

  getLastBranchForBlueprint(blueprint: string) {
    return this.blueprintBranchMap[blueprint];
  }

  updateURLParams(urlParams: ActivatedRouteSnapshot) {
    this.currentURLParams = urlParams;
    this.URLParamsUpdates.next({ urlParams: this.currentURLParams });
  }

  emitBranchDataLoadedSignal() {
    this.branchDataLoaded.next(true);
  }
}
